/**
 * Ficha operativa de mesa — una verdad por pantalla.
 * Mozo o encargado: monitorean + operan (si Tables.operate).
 * Sumar/Cobrar viven en el footer del ModalShell (no acá).
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, Text, TouchableOpacity, View, StyleSheet } from 'react-native';
import { spacing } from '../../shared/theme/spacing';
import { typography } from '../../shared/theme/typography';
import { ticketColors } from '../../shared/theme/ticketColors';
import { showError, showInfo, showSuccess } from '../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import type { OperationalConfirmOptions } from '../../shared/ui/useOperationalConfirm';
import type {
  GuestSignal,
  StaffTableAccount,
  StaffTableDraftPayload,
  StaffVisitSale,
  TableOrderDraft,
  TableOrderDraftLine,
} from '../domain/hospitalityTypes';
import {
  openTableSession,
  getOpenTableSession,
  staffAckGuestSignal,
  staffConfirmTableDraftRound,
  staffDraftClear,
  staffDraftRemoveLine,
  staffDraftUpsertLine,
  staffGetTableDraft,
  syncTableSessionKitchenSentFromOpenTickets,
  staffLiberateTableDraft,
  updateTableSessionCovers,
} from '../infra/hospitalityRepo';
import { mapCartLikeToPreCuentaLines } from './buildPreCuenta';
import { mesaVisualStateMeta, resolveMesaVisualState } from '../domain/mesaVisualStatus';
import {
  printPreCuentaDocumentSafe,
  sharePreCuentaDocumentSafe,
} from '../../printing/application/printComandaDocuments';
import { formatMoney } from '../../shared/format/public-api';
import { useSessionStore } from '../../shell/session/store/useSessionStore';

type AccountLine = {
  name: string;
  quantity: number;
  price: number;
  optionsLabel?: string | null;
  lineNote?: string | null;
  kitchenSent?: boolean;
};

type Props = {
  branchId: string;
  diningTableId: string;
  tableName: string;
  /** Capacidad del plano — default al abrir; la sesión guarda covers reales. */
  tableCapacity: number;
  canOperate: boolean;
  confirm: (options: OperationalConfirmOptions) => Promise<boolean>;
  liveRevision?: string;
  onChanged: () => Promise<void> | void;
  /** Abre carta (footer o CTA en Pendiente vacío). */
  onSumar?: () => void;
  /** Mozo asignado (nombre) — fila de la ficha. */
  waiterName?: string | null;
  /** Abre el selector de mozo (Más acciones del padre). */
  onAssignWaiter?: () => void;
  /** Hay un intento de pago QR pendiente en la sesión. */
  paymentPending?: boolean;
  /** Cancela el intento de pago pendiente (limpieza interna). */
  onCancelPayment?: () => void;
  onAccountMeta?: (meta: {
    needsLiberate: boolean;
    accountTotal: number;
    hasOpenSession: boolean;
    canCobrar: boolean;
    statusLabel: string;
  }) => void;
};

function signalLabel(kind: string): string {
  if (kind === 'ready_to_order') return 'Ya pidieron';
  if (kind === 'call_waiter') return 'Llaman al mozo';
  if (kind === 'request_bill') return 'Piden la cuenta';
  return kind;
}

function whoLine(role: string): string {
  if (role === 'staff') return 'por: Mozo';
  if (role === 'guest') return 'por: Cliente';
  return `por: ${role}`;
}

function formatDuration(fromIso: string | null | undefined): string | null {
  if (!fromIso) return null;
  const ms = Date.now() - new Date(fromIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const totalMin = Math.floor(ms / 60_000);
  if (totalMin < 1) return '<1 min';
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h < 1) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

function mapAccountLines(cartJson: unknown[] | undefined): AccountLine[] {
  if (!Array.isArray(cartJson)) return [];
  return cartJson.map((raw) => {
    const row = (raw ?? {}) as Record<string, unknown>;
    const menuOptions = Array.isArray(row.menuOptions) ? row.menuOptions : [];
    const optionsLabel =
      menuOptions.length > 0
        ? menuOptions
            .map((o) => {
              const opt = o as Record<string, unknown>;
              const name = String(opt.choiceName ?? '');
              const qty = Number(opt.qty) || 1;
              return qty > 1 ? `${name}×${qty}` : name;
            })
            .filter(Boolean)
            .join(', ')
        : null;
    return {
      name: String(row.name ?? 'Ítem'),
      quantity: Number(row.quantity) || 1,
      price: Number(row.price) || 0,
      optionsLabel,
      lineNote: row.lineNote != null ? String(row.lineNote) : null,
      kitchenSent: Boolean(row.kitchenSent),
    };
  });
}

function money(n: number): string {
  return `${formatMoney(Math.round(n))}`;
}

function formatClock(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
}

const VISIT_SALES_PREVIEW = 4;
const VISIT_LINES_PREVIEW = 6;
const LIVE_LINES_PREVIEW = 8;

export function TableGuestInboxPanel({
  branchId,
  diningTableId,
  tableName,
  tableCapacity,
  canOperate,
  confirm,
  liveRevision,
  onChanged,
  onSumar,
  waiterName,
  onAssignWaiter,
  paymentPending,
  onCancelPayment,
  onAccountMeta,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const scope = useMemo(() => ({ branchId, diningTableId }), [branchId, diningTableId]);
  const [loaded, setLoaded] = useState<{ scope: typeof scope; payload: StaffTableDraftPayload } | null>(null);
  const payload = loaded?.scope === scope ? loaded.payload : null;
  const scopeRef = React.useRef<typeof scope | null>(null);
  const requestRef = React.useRef(0);
  const [expandedSaleId, setExpandedSaleId] = useState<string | null>(null);
  const [paidSectionOpen, setPaidSectionOpen] = useState(true);
  const [showAllVisitSales, setShowAllVisitSales] = useState(false);
  const [linesExpandedBySale, setLinesExpandedBySale] = useState<Record<string, boolean>>({});
  const [showAllEnMesa, setShowAllEnMesa] = useState(false);
  const [showAllPending, setShowAllPending] = useState(false);
  const [preCuentaBusy, setPreCuentaBusy] = useState<'print' | 'share' | null>(null);
  const [coversCount, setCoversCount] = useState(() => Math.max(1, tableCapacity || 1));
  const [coversBusy, setCoversBusy] = useState(false);
  const coversDirtyRef = React.useRef(false);
  const loadedOnceRef = React.useRef(false);
  const onAccountMetaRef = React.useRef(onAccountMeta);
  onAccountMetaRef.current = onAccountMeta;
  const branchName = useSessionStore(
    (s) => s.branches.find((b) => b.id === branchId)?.name?.trim() || null,
  );

  useLayoutEffect(() => {
    scopeRef.current = scope;
    setLoading(true);
    return () => {
      if (scopeRef.current === scope) scopeRef.current = null;
    };
  }, [scope]);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (scopeRef.current !== scope) return;
    const request = ++requestRef.current;
    const isCurrent = () => scopeRef.current === scope && requestRef.current === request;
    const silent = Boolean(opts?.silent);
    if (!silent) setLoading(true);
    try {
      let next = await staffGetTableDraft(branchId, diningTableId);
      if (!isCurrent()) return;
      if ((next.account?.openKitchenCount ?? 0) > 0) {
        const marked = await syncTableSessionKitchenSentFromOpenTickets(diningTableId).catch(
          () => 0,
        );
        if (!isCurrent()) return;
        if (marked > 0) {
          next = await staffGetTableDraft(branchId, diningTableId);
          if (!isCurrent()) return;
        }
      }
      setLoaded({ scope, payload: next });
      loadedOnceRef.current = true;
    } catch (error) {
      if (!isCurrent()) return;
      presentUnexpectedFailure({
        module: 'hospitality',
        op: 'tableside.staff_get_draft',
        error,
        userMessage: 'No se pudo cargar el pedido de la mesa.',
      });
      setLoaded(null);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [branchId, diningTableId, scope]);

  useEffect(() => {
    loadedOnceRef.current = false;
    setExpandedSaleId(null);
    setPaidSectionOpen(true);
    setShowAllVisitSales(false);
    setLinesExpandedBySale({});
    setShowAllEnMesa(false);
    setShowAllPending(false);
    coversDirtyRef.current = false;
    setCoversCount(Math.max(1, tableCapacity || 1));
    void load({ silent: false });
  }, [diningTableId, tableCapacity, load]);

  useEffect(() => {
    if (!loadedOnceRef.current || liveRevision == null) return;
    void load({ silent: true });
  }, [liveRevision, load]);

  const draft: TableOrderDraft | null = payload?.draft ?? null;
  const lines = draft?.lines ?? [];
  const signals: GuestSignal[] = payload?.openSignals ?? [];
  const looking = payload?.presence?.length ?? 0;
  const account: StaffTableAccount | null = payload?.account ?? null;

  useEffect(() => {
    if (coversDirtyRef.current) return;
    if (account?.sessionStatus === 'open' && account.covers != null) {
      setCoversCount(Math.max(1, Number(account.covers) || 1));
    }
  }, [account?.covers, account?.sessionStatus]);
  const visitSales: StaffVisitSale[] = payload?.visitSales ?? [];
  const accountLines = useMemo(() => mapAccountLines(account?.cartJson), [account?.cartJson]);

  const draftTotal = lines.reduce((sum, l) => sum + l.unitPrice * l.qty, 0);
  const visitPaidTotal = visitSales.reduce((sum, s) => sum + s.total, 0);
  const accountTotal = accountLines.reduce((sum, l) => sum + l.price * l.quantity, 0);
  const needsLiberate = Boolean(account?.needsLiberate);
  const hasOpenSession = account?.sessionStatus === 'open';
  const openKitchenCount = account?.openKitchenCount ?? 0;
  const readyKitchenCount = account?.readyKitchenCount ?? 0;
  const kitchenReadyLines = account?.kitchenReadyLines ?? [];
  const cookingCount = Math.max(0, openKitchenCount - readyKitchenCount);
  const accountIsPaidSnapshot =
    needsLiberate && account?.sessionStatus === 'closed';
  const hasChargeableSession = hasOpenSession && accountLines.length > 0;
  const postPayIdle = needsLiberate && lines.length === 0 && !hasChargeableSession;
  const canCobrar = lines.length > 0 || hasChargeableSession;
  const unpaidAccount = hasChargeableSession && !postPayIdle;
  const openedAgo = formatDuration(account?.openedAt ?? account?.occupiedAt);
  // Open vacía post-Cobrar: no inventar bloque En mesa vacío (el RPC cierra el zombie).
  const liveAccountLines = accountIsPaidSnapshot || !hasChargeableSession ? [] : accountLines;
  const showEnMesaBlock = liveAccountLines.length > 0;
  const asksForBill = signals.some((s) => s.kind === 'request_bill');
  const canShowPreCuentaActions = liveAccountLines.length > 0 || (asksForBill && accountLines.length > 0);
  const preCuentaSourceLines = liveAccountLines.length > 0 ? liveAccountLines : accountLines;
  const visibleVisitSales = showAllVisitSales
    ? visitSales
    : visitSales.slice(0, VISIT_SALES_PREVIEW);
  const hiddenVisitSalesCount = Math.max(0, visitSales.length - visibleVisitSales.length);
  const visualState = resolveMesaVisualState({
    tableStatus: account?.tableStatus ?? 'available',
    occupiedAt: account?.occupiedAt ?? null,
    hasOpenSession: hasChargeableSession,
    draftCount: lines.length,
    lookingCount: looking,
    needsLiberate,
    hasActiveReservation: account?.tableStatus === 'reserved',
    reservationOverdue: false,
  });
  const statusLabel =
    visualState === 'free' ? 'Sin pedido' : mesaVisualStateMeta(visualState).label;
  const visitTotalValue =
    visitSales.length > 0 && !hasChargeableSession
      ? draftTotal > 0
        ? draftTotal
        : visitPaidTotal
      : accountTotal + draftTotal;
  const visitTotalLabel =
    visitSales.length > 0 && !hasChargeableSession && draftTotal === 0
      ? 'Cobrado'
      : draftTotal > 0 && !hasChargeableSession
        ? 'A cobrar'
        : 'Total';
  const canShowLiberate =
    canOperate &&
    (needsLiberate ||
      looking > 0 ||
      lines.length > 0 ||
      accountLines.length > 0 ||
      hasChargeableSession ||
      openKitchenCount > 0 ||
      visitSales.length > 0 ||
      account?.tableStatus === 'occupied');

  const guestDevices = new Set(
    lines
      .filter((l) => l.addedByRole === 'guest' && l.addedByDeviceId)
      .map((l) => l.addedByDeviceId as string),
  );
  const hasStaffLines = lines.some((l) => l.addedByRole === 'staff');
  const showWho = hasStaffLines || guestDevices.size > 1;

  useEffect(() => {
    onAccountMetaRef.current?.({
      needsLiberate,
      accountTotal,
      hasOpenSession: hasChargeableSession,
      canCobrar,
      statusLabel,
    });
  }, [accountTotal, canCobrar, hasChargeableSession, needsLiberate, statusLabel]);

  const bumpCovers = useCallback(
    async (delta: number) => {
      const next = Math.max(1, Math.min(99, coversCount + delta));
      if (next === coversCount || coversBusy) return;
      const prev = coversCount;
      coversDirtyRef.current = true;
      setCoversCount(next);
      if (!canOperate) {
        coversDirtyRef.current = false;
        return;
      }
      setCoversBusy(true);
      try {
        let sessionId = account?.sessionId ?? null;
        const sessionOpen = account?.sessionStatus === 'open' && Boolean(sessionId);

        if (sessionOpen && sessionId) {
          await updateTableSessionCovers(sessionId, next);
        } else {
          // Sin cuenta abierta: abrir/asegurar sesión con este N (ocupa la mesa).
          const session = await openTableSession({
            branchId,
            diningTableId,
            covers: next,
          });
          sessionId = session.id;
          if (session.covers !== next) {
            await updateTableSessionCovers(session.id, next);
          }
        }
        await load({ silent: true });
        void onChanged();
        coversDirtyRef.current = false;
      } catch (error) {
        setCoversCount(prev);
        coversDirtyRef.current = false;
        presentUnexpectedFailure({
          module: 'hospitality',
          op: 'tableside.session_covers',
          error,
          userMessage: 'No se pudieron actualizar los cubiertos.',
        });
      } finally {
        setCoversBusy(false);
      }
    },
    [
      account?.sessionId,
      account?.sessionStatus,
      branchId,
      canOperate,
      coversBusy,
      coversCount,
      diningTableId,
      load,
      onChanged,
    ],
  );

  const buildTablePreCuenta = useCallback(async () => {
    const linesForDoc = mapCartLikeToPreCuentaLines(preCuentaSourceLines);
    const subtotal = linesForDoc.reduce((sum, l) => sum + l.lineTotal, 0);
    let tipAmount = Math.max(0, Number(account?.tipIntent?.amount ?? 0) || 0);
    if (tipAmount <= 0 && hasOpenSession && diningTableId) {
      try {
        const session = await getOpenTableSession(diningTableId);
        tipAmount = Math.max(0, Number(session?.tipIntent?.amount ?? 0) || 0);
      } catch {
        // Best-effort: imprimir sin tip si la sesión no responde.
      }
    }
    return {
      lines: linesForDoc,
      subtotal,
      tipAmount: tipAmount > 0 ? tipAmount : undefined,
      total: subtotal + tipAmount,
      covers: coversCount,
      openedAt: account?.openedAt ?? account?.occupiedAt ?? null,
    };
  }, [
    account?.occupiedAt,
    account?.openedAt,
    account?.tipIntent?.amount,
    coversCount,
    diningTableId,
    hasOpenSession,
    preCuentaSourceLines,
  ]);

  const printTablePreCuenta = useCallback(async () => {
    if (preCuentaSourceLines.length === 0) {
      showInfo('Sin cuenta', 'Todavía no hay ítems para imprimir.');
      return;
    }
    setPreCuentaBusy('print');
    try {
      const doc = await buildTablePreCuenta();
      await printPreCuentaDocumentSafe({
        branchId,
        tableName,
        covers: doc.covers,
        lines: doc.lines,
        subtotal: doc.subtotal,
        tipAmount: doc.tipAmount,
        total: doc.total,
        openedAt: doc.openedAt,
        context: 'mesa',
        branchName,
        printedAt: new Date().toISOString(),
        onError: showError,
      });
    } finally {
      setPreCuentaBusy(null);
    }
  }, [branchId, branchName, buildTablePreCuenta, preCuentaSourceLines.length, tableName]);

  const shareTablePreCuenta = useCallback(async () => {
    if (preCuentaSourceLines.length === 0) {
      showInfo('Sin cuenta', 'Todavía no hay ítems para compartir.');
      return;
    }
    setPreCuentaBusy('share');
    try {
      const doc = await buildTablePreCuenta();
      await sharePreCuentaDocumentSafe({
        branchId,
        tableName,
        covers: doc.covers,
        lines: doc.lines,
        subtotal: doc.subtotal,
        tipAmount: doc.tipAmount,
        total: doc.total,
        openedAt: doc.openedAt,
        context: 'mesa',
        branchName,
        printedAt: new Date().toISOString(),
        onError: showError,
        onCopied: () => showSuccess('Cuenta', 'Copiada'),
      });
    } finally {
      setPreCuentaBusy(null);
    }
  }, [branchId, branchName, buildTablePreCuenta, preCuentaSourceLines.length, tableName]);

  const run = async (fn: () => Promise<void>, okMessage?: string) => {
    if (!canOperate) return;
    setBusy(true);
    try {
      await fn();
      if (okMessage) showInfo(tableName, okMessage);
      await load();
      await onChanged();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Reintentá.';
      if (msg.includes('tableside_draft_empty')) {
        showInfo('Pedido vacío', 'No hay nada para pasar a cocina.');
      } else if (msg.includes('tableside_draft_not_empty')) {
        showInfo('Hay ítems', 'Vacía el pedido o liberá con fuerza.');
      } else if (msg.includes('tableside_session_open')) {
        showInfo('Cuenta abierta', 'Cerrá la cuenta en POS o liberá con fuerza.');
      } else {
        presentUnexpectedFailure({
          module: 'hospitality',
          op: 'tableside.staff_action',
          error,
          userMessage: 'No se pudo completar la acción.',
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const doLiberate = async (force: boolean) => {
    await staffLiberateTableDraft({ branchId, diningTableId, force });
  };

  const liberate = () => {
    const needsForce =
      lines.length > 0 || hasOpenSession || unpaidAccount || openKitchenCount > 0;
    void confirm({
      title: '¿Liberar mesa?',
      message: (() => {
        if (openKitchenCount > 0 && unpaidAccount) {
          return 'Hay cocina en curso y cuenta sin cobrar. Liberar cierra la visita; la comanda queda abierta en Cocina con aviso de mesa liberada.';
        }
        if (openKitchenCount > 0) {
          return 'Hay cocina en curso. Liberar cierra la visita; la comanda queda abierta en Cocina con aviso de mesa liberada.';
        }
        if (unpaidAccount) {
          return 'Hay cuenta abierta sin cobrar. Liberar cierra la visita.';
        }
        if (lines.length > 0) {
          return 'Hay pedido pendiente. Liberar lo descarta.';
        }
        if (needsLiberate) {
          return 'La mesa queda libre otra vez.';
        }
        return 'Se limpia la mesa y queda libre.';
      })(),
      confirmLabel: 'Liberar',
      destructive: true,
      presentation: 'overlay',
    }).then(async (ok) => {
      if (!ok) return;
      await run(() => doLiberate(needsForce), 'Mesa liberada');
    });
  };

  const changeLineQty = (line: TableOrderDraftLine, nextQty: number) => {
    if (!canOperate || busy) return;
    void run(async () => {
      if (nextQty <= 0) {
        await staffDraftRemoveLine({
          branchId,
          diningTableId,
          clientLineId: line.clientLineId,
        });
        return;
      }
      await staffDraftUpsertLine({
        branchId,
        diningTableId,
        clientLineId: line.clientLineId,
        productId: line.productId ?? '',
        name: line.name,
        qty: nextQty,
        unitPrice: line.unitPrice,
        unitNote: line.unitNote,
        optionsJson: line.optionsJson,
        optionsLabel: line.optionsLabel,
      });
    });
  };

  const removeLine = (line: TableOrderDraftLine) => {
    if (!canOperate || busy) return;
    void run(async () => {
      await staffDraftRemoveLine({
        branchId,
        diningTableId,
        clientLineId: line.clientLineId,
      });
    }, 'Ítem quitado');
  };

  if (loading) {
    return (
      <View style={styles.box} testID="hospitality.mesas.guest-inbox-loading">
        <ActivityIndicator color={ticketColors.gold} />
        <Text style={styles.hint}>Cargando la mesa…</Text>
      </View>
    );
  }

  const emptyVisit =
    accountLines.length === 0 &&
    lines.length === 0 &&
    visitSales.length === 0 &&
    !needsLiberate &&
    looking === 0;
  const metaBits = [
    openedAgo ? `Abierta hace ${openedAgo}` : null,
    looking > 0
      ? looking === 1
        ? '1 mirando la carta'
        : `${looking} mirando la carta`
      : null,
    cookingCount > 0
      ? cookingCount === 1
        ? '1 en cocina'
        : `${cookingCount} en cocina`
      : null,
    readyKitchenCount > 0
      ? readyKitchenCount === 1
        ? '1 listo'
        : `${readyKitchenCount} listos`
      : null,
  ].filter(Boolean);

  // Post-cobro sin sesión: solo lectura. Libre / en mesa / armando: editable.
  const coversEditable =
    canOperate && !coversBusy && (hasOpenSession || (!needsLiberate && !postPayIdle));
  const capacityHint =
    tableCapacity > 0 && tableCapacity !== coversCount
      ? `Mesa para ${tableCapacity}`
      : null;

  return (
    <View style={styles.box} testID="hospitality.mesas.guest-inbox">
      {/* Header único */}
      <View style={styles.visitMeta}>
        <View style={styles.visitMetaMain}>
          <Text style={styles.visitTitle}>{statusLabel}</Text>
          <Text style={styles.visitSub}>{metaBits.join(' · ') || '—'}</Text>
        </View>
        {visitSales.length > 0 || visitTotalValue > 0 || draftTotal > 0 || accountTotal > 0 ? (
          <View style={styles.visitTotalBlock} testID="hospitality.mesas.visit-total">
            <Text style={styles.visitTotalLabel}>{visitTotalLabel}</Text>
            <Text style={styles.visitTotalValue}>{money(visitTotalValue)}</Text>
          </View>
        ) : null}
      </View>

      {waiterName !== undefined ? (
        <View style={styles.mozoRow} testID="hospitality.mesas.mozo">
          <Text style={styles.mozoLabel}>
            Mozo: <Text style={styles.mozoValue}>{waiterName ?? 'sin asignar'}</Text>
          </Text>
          {canOperate && onAssignWaiter ? (
            <TouchableOpacity
              onPress={onAssignWaiter}
              accessibilityRole="button"
              accessibilityLabel={waiterName ? 'Cambiar mozo' : 'Asignar mozo'}
            >
              <Text style={styles.mozoBtnLabel}>{waiterName ? 'Cambiar' : 'Asignar'}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      <View style={styles.coversRow} testID="hospitality.mesas.covers">
        <View style={styles.coversTextCol}>
          <Text style={styles.coversLabel}>Cubiertos</Text>
          <Text style={styles.coversHint}>
            {coversEditable
              ? capacityHint ?? 'Cuántos hay en la mesa'
              : `${coversCount} ${coversCount === 1 ? 'persona' : 'personas'}`}
          </Text>
        </View>
        {coversEditable ? (
          <View style={styles.coversStepper}>
            <TouchableOpacity
              style={[styles.coversBtn, (coversBusy || coversCount <= 1) && styles.btnDisabled]}
              disabled={coversBusy || coversCount <= 1}
              onPress={() => void bumpCovers(-1)}
              accessibilityRole="button"
              accessibilityLabel="Menos cubiertos"
              testID="hospitality.mesas.covers-dec"
            >
              <Text style={styles.coversBtnLabel}>−</Text>
            </TouchableOpacity>
            <Text style={styles.coversValue} testID="hospitality.mesas.covers-value">
              {coversBusy ? '…' : coversCount}
            </Text>
            <TouchableOpacity
              style={[styles.coversBtn, (coversBusy || coversCount >= 99) && styles.btnDisabled]}
              disabled={coversBusy || coversCount >= 99}
              onPress={() => void bumpCovers(1)}
              accessibilityRole="button"
              accessibilityLabel="Más cubiertos"
              testID="hospitality.mesas.covers-inc"
            >
              <Text style={styles.coversBtnLabel}>+</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <Text style={styles.coversValueRead} testID="hospitality.mesas.covers-value">
            {coversCount}
          </Text>
        )}
      </View>

      {signals.length > 0 ? (
        <View style={[styles.block, styles.signalBlock]} testID="hospitality.mesas.signals">
          {signals.map((s) => (
            <View key={s.id} style={styles.signalRow}>
              <Text style={styles.signalLabel}>{signalLabel(s.kind)}</Text>
              {canOperate ? (
                <TouchableOpacity
                  style={[styles.ackBtn, busy && styles.btnDisabled]}
                  onPress={() =>
                    void run(async () => {
                      await staffAckGuestSignal(branchId, s.id);
                    }, 'Listo, atendido')
                  }
                  disabled={busy}
                  accessibilityRole="button"
                  accessibilityLabel="Ya lo atendí · quitar aviso"
                >
                  <Text style={styles.ackBtnLabel}>Ya lo atendí</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          ))}
        </View>
      ) : null}

      {kitchenReadyLines.length > 0 ? (
        <View style={[styles.block, styles.readyBlock]}>
          <Text style={styles.blockTitle}>Listo para servir</Text>
          {kitchenReadyLines.map((line, idx) => (
            <Text key={`ready-${idx}-${line.name}`} style={styles.readyLine}>
              {line.qty} × {line.name}
            </Text>
          ))}
        </View>
      ) : null}

      {/* Cobros de la visita — sección colapsable, filas planas */}
      {visitSales.length > 0 ? (
        <View style={styles.block} testID="hospitality.mesas.visit-sales">
          <Pressable
            onPress={() => setPaidSectionOpen((v) => !v)}
            accessibilityRole="button"
            accessibilityLabel={paidSectionOpen ? 'Ocultar cobros' : 'Ver cobros'}
            style={styles.collapseHeader}
          >
            <Text style={styles.blockTitle}>
              Cobrado
              {visitSales.length > 1 ? ` · ${visitSales.length}` : ''}
              {' · '}
              {money(visitPaidTotal)}
            </Text>
            <Text style={styles.collapseChevron}>{paidSectionOpen ? '▾' : '▸'}</Text>
          </Pressable>
          {paidSectionOpen
            ? visibleVisitSales.map((sale) => {
                const canExpand = sale.lines.length > 1;
                const open = canExpand && expandedSaleId === sale.saleId;
                const clock = formatClock(sale.closedAt);
                const showAllLines = Boolean(linesExpandedBySale[sale.saleId]);
                const visibleLines = showAllLines
                  ? sale.lines
                  : sale.lines.slice(0, VISIT_LINES_PREVIEW);
                const hiddenLines = Math.max(0, sale.lines.length - visibleLines.length);
                const singleName =
                  sale.lines.length === 1 ? sale.lines[0]?.name ?? null : null;
                const summary =
                  singleName != null
                    ? singleName
                    : sale.lineCount === 1
                      ? '1 ítem'
                      : `${sale.lineCount} ítems`;
                return (
                  <View key={sale.saleId} style={styles.visitSaleRow}>
                    <Pressable
                      onPress={
                        canExpand
                          ? () =>
                              setExpandedSaleId((cur) =>
                                cur === sale.saleId ? null : sale.saleId,
                              )
                          : undefined
                      }
                      disabled={!canExpand}
                      accessibilityRole={canExpand ? 'button' : undefined}
                      accessibilityLabel={
                        canExpand
                          ? open
                            ? 'Ocultar ítems del cobro'
                            : 'Ver ítems del cobro'
                          : undefined
                      }
                      style={styles.visitSaleHeader}
                    >
                      <Text style={[styles.visitSaleTitle, { flex: 1, minWidth: 0 }]} numberOfLines={1}>
                        {clock ? `${clock} · ${summary}` : summary}
                      </Text>
                      <Text style={styles.visitSaleTotal}>{money(sale.total)}</Text>
                      {canExpand ? (
                        <Text style={styles.collapseChevron}>{open ? '▾' : '▸'}</Text>
                      ) : null}
                    </Pressable>
                    {open ? (
                      <View style={styles.visitSaleBody}>
                        {visibleLines.map((line, idx) => (
                          <View key={`${sale.saleId}-${idx}-${line.name}`} style={styles.lineRow}>
                            <View style={{ flex: 1, gap: 2 }}>
                              <Text style={styles.lineName}>
                                {line.qty} × {line.name}
                              </Text>
                              {line.optionsLabel ? (
                                <Text style={styles.lineMeta}>{line.optionsLabel}</Text>
                              ) : null}
                              {line.lineNote ? (
                                <Text style={styles.lineMeta}>Nota: {line.lineNote}</Text>
                              ) : null}
                            </View>
                            <Text style={styles.linePrice}>{money(line.price * line.qty)}</Text>
                          </View>
                        ))}
                        {hiddenLines > 0 ? (
                          <TouchableOpacity
                            onPress={() =>
                              setLinesExpandedBySale((prev) => ({
                                ...prev,
                                [sale.saleId]: true,
                              }))
                            }
                            accessibilityRole="button"
                            accessibilityLabel={`Ver ${hiddenLines} ítems más`}
                          >
                            <Text style={styles.moreLink}>Ver {hiddenLines} más</Text>
                          </TouchableOpacity>
                        ) : null}
                        {showAllLines && sale.lines.length > VISIT_LINES_PREVIEW ? (
                          <TouchableOpacity
                            onPress={() =>
                              setLinesExpandedBySale((prev) => ({
                                ...prev,
                                [sale.saleId]: false,
                              }))
                            }
                            accessibilityRole="button"
                            accessibilityLabel="Mostrar menos ítems"
                          >
                            <Text style={styles.moreLink}>Mostrar menos</Text>
                          </TouchableOpacity>
                        ) : null}
                      </View>
                    ) : null}
                  </View>
                );
              })
            : null}
          {paidSectionOpen && hiddenVisitSalesCount > 0 ? (
            <TouchableOpacity
              onPress={() => setShowAllVisitSales(true)}
              accessibilityRole="button"
              accessibilityLabel={`Ver ${hiddenVisitSalesCount} cobros anteriores`}
              style={styles.moreSalesBtn}
            >
              <Text style={styles.moreLink}>
                Ver {hiddenVisitSalesCount} más
              </Text>
            </TouchableOpacity>
          ) : null}
          {paidSectionOpen && showAllVisitSales && visitSales.length > VISIT_SALES_PREVIEW ? (
            <TouchableOpacity
              onPress={() => setShowAllVisitSales(false)}
              accessibilityRole="button"
              accessibilityLabel="Mostrar menos cobros"
              style={styles.moreSalesBtn}
            >
              <Text style={styles.moreLink}>Mostrar menos</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      {/* Cuenta viva */}
      {showEnMesaBlock ? (
        <View style={styles.block}>
          <View style={styles.blockHeader}>
            <Text style={styles.blockTitle}>
              En mesa
              {liveAccountLines.length > 0 ? ` · ${liveAccountLines.length}` : ''}
            </Text>
            {cookingCount > 0 ? (
              <Text style={styles.blockBadge}>
                {cookingCount === 1 ? '1 en cocina' : `${cookingCount} en cocina`}
              </Text>
            ) : readyKitchenCount > 0 ? (
              <Text style={styles.blockBadge}>
                {readyKitchenCount === 1 ? '1 listo' : `${readyKitchenCount} listos`}
              </Text>
            ) : null}
          </View>
          {liveAccountLines
            .slice(0, showAllEnMesa ? liveAccountLines.length : LIVE_LINES_PREVIEW)
            .map((line, idx) => (
              <View key={`acct-${idx}-${line.name}`} style={styles.lineRow}>
                <View style={{ flex: 1, gap: 2 }}>
                  <View style={styles.lineTitleRow}>
                    <Text style={styles.lineName}>
                      {line.quantity} × {line.name}
                    </Text>
                    {line.kitchenSent && readyKitchenCount > 0 && openKitchenCount === readyKitchenCount ? (
                      <View style={styles.pillReady}>
                        <Text style={styles.pillReadyLabel}>Listo</Text>
                      </View>
                    ) : line.kitchenSent && openKitchenCount > 0 ? (
                      <View style={styles.pillKitchen}>
                        <Text style={styles.pillKitchenLabel}>En cocina</Text>
                      </View>
                    ) : (
                      <View style={styles.pillAccount}>
                        <Text style={styles.pillAccountLabel}>Sin cobrar</Text>
                      </View>
                    )}
                  </View>
                  {line.optionsLabel ? <Text style={styles.lineMeta}>{line.optionsLabel}</Text> : null}
                  {line.lineNote ? <Text style={styles.lineMeta}>Nota: {line.lineNote}</Text> : null}
                </View>
                <Text style={styles.linePrice}>{money(line.price * line.quantity)}</Text>
              </View>
            ))}
          {!showAllEnMesa && liveAccountLines.length > LIVE_LINES_PREVIEW ? (
            <TouchableOpacity
              onPress={() => setShowAllEnMesa(true)}
              accessibilityRole="button"
              accessibilityLabel="Ver más ítems en mesa"
            >
              <Text style={styles.moreLink}>
                Ver {liveAccountLines.length - LIVE_LINES_PREVIEW} más
              </Text>
            </TouchableOpacity>
          ) : null}
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Subtotal</Text>
            <Text style={styles.totalValue}>{money(accountTotal)}</Text>
          </View>
          {canShowPreCuentaActions ? (
            <View style={styles.preCuentaActions}>
              <TouchableOpacity
                style={[styles.preCuentaBtn, (busy || preCuentaBusy != null) && styles.btnDisabled]}
                disabled={busy || preCuentaBusy != null}
                onPress={() => void printTablePreCuenta()}
                accessibilityRole="button"
                accessibilityLabel="Imprimir cuenta"
                testID="hospitality.mesas.imprimir-cuenta"
              >
                <Text style={styles.preCuentaBtnLabel}>
                  {preCuentaBusy === 'print' ? 'Imprimiendo…' : 'Imprimir cuenta'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.preCuentaBtn, (busy || preCuentaBusy != null) && styles.btnDisabled]}
                disabled={busy || preCuentaBusy != null}
                onPress={() => void shareTablePreCuenta()}
                accessibilityRole="button"
                accessibilityLabel="Compartir cuenta"
                testID="hospitality.mesas.compartir-cuenta"
              >
                <Text style={styles.preCuentaBtnLabel}>
                  {preCuentaBusy === 'share' ? 'Compartiendo…' : 'Compartir cuenta'}
                </Text>
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
      ) : null}

      {/* Pendiente = borrador editable */}
      <View style={[styles.block, styles.draftBlock]}>
        <View style={styles.blockHeader}>
          <Text style={styles.blockTitle}>
            Pendiente
            {lines.length > 0 ? ` · ${lines.length}` : ''}
          </Text>
        </View>
        {lines.length === 0 ? (
          <View style={styles.emptyPending}>
            <Text style={styles.hintMuted}>
              {emptyVisit ? 'Todavía no hay pedido.' : 'Nada pendiente'}
            </Text>
            {canOperate && onSumar ? (
              <TouchableOpacity
                style={[styles.sumarLink, busy && styles.btnDisabled]}
                disabled={busy}
                onPress={onSumar}
                accessibilityRole="button"
                accessibilityLabel="Sumar a la mesa"
                testID="hospitality.mesas.inbox-sumar-empty"
              >
                <Text style={styles.sumarLinkLabel}>Sumar a la mesa</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ) : (
          <>
            {lines
              .slice(0, showAllPending ? lines.length : LIVE_LINES_PREVIEW)
              .map((line) => (
            <View key={line.id} style={styles.draftLine}>
              <View style={styles.draftLineMain}>
                <View style={styles.draftLineTop}>
                  <Text style={styles.lineName} numberOfLines={2}>
                    {canOperate ? null : `${line.qty} × `}
                    {line.name}
                  </Text>
                  {canOperate ? (
                    <View style={styles.qtyRow}>
                      <TouchableOpacity
                        style={[styles.qtyBtn, busy && styles.btnDisabled]}
                        disabled={busy}
                        onPress={() => changeLineQty(line, line.qty - 1)}
                        accessibilityRole="button"
                        accessibilityLabel="Restar"
                      >
                        <Text style={styles.qtyBtnLabel}>−</Text>
                      </TouchableOpacity>
                      <Text style={styles.qtyValue}>{line.qty}</Text>
                      <TouchableOpacity
                        style={[styles.qtyBtn, busy && styles.btnDisabled]}
                        disabled={busy}
                        onPress={() => changeLineQty(line, line.qty + 1)}
                        accessibilityRole="button"
                        accessibilityLabel="Sumar uno"
                      >
                        <Text style={styles.qtyBtnLabel}>+</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.removeBtn, busy && styles.btnDisabled]}
                        disabled={busy}
                        onPress={() => removeLine(line)}
                        accessibilityRole="button"
                        accessibilityLabel="Quitar ítem"
                        hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
                      >
                        <Text style={styles.removeBtnLabel}>Quitar</Text>
                      </TouchableOpacity>
                    </View>
                  ) : null}
                  <Text style={styles.linePrice}>{money(line.unitPrice * line.qty)}</Text>
                </View>
                {line.optionsLabel ? <Text style={styles.lineMeta}>{line.optionsLabel}</Text> : null}
                {line.unitNote ? <Text style={styles.lineMeta}>Nota: {line.unitNote}</Text> : null}
                {showWho ? <Text style={styles.lineWho}>{whoLine(line.addedByRole)}</Text> : null}
              </View>
            </View>
              ))}
            {!showAllPending && lines.length > LIVE_LINES_PREVIEW ? (
              <TouchableOpacity
                onPress={() => setShowAllPending(true)}
                accessibilityRole="button"
                accessibilityLabel="Ver más pendientes"
              >
                <Text style={styles.moreLink}>
                  Ver {lines.length - LIVE_LINES_PREVIEW} más
                </Text>
              </TouchableOpacity>
            ) : null}
          </>
        )}
        {lines.length > 0 && (liveAccountLines.length > 0 || hasChargeableSession) ? (
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Pendiente</Text>
            <Text style={styles.totalValue}>{money(draftTotal)}</Text>
          </View>
        ) : null}
      </View>

      {canOperate ? (
        <View style={styles.actions}>
          {lines.length > 0 ? (
            <TouchableOpacity
              style={[styles.primaryBtn, busy && styles.btnDisabled]}
              disabled={busy}
              onPress={() =>
                void run(async () => {
                  await staffConfirmTableDraftRound({
                    branchId,
                    diningTableId,
                    covers: coversCount,
                  });
                }, 'Pasó a cocina')
              }
              accessibilityRole="button"
              accessibilityLabel="Pasar a cocina"
              testID="hospitality.mesas.draft-confirm"
            >
              <Text style={styles.primaryLabel}>{busy ? '…' : 'Pasar a cocina'}</Text>
            </TouchableOpacity>
          ) : null}

          {lines.length > 0 ? (
            <TouchableOpacity
              style={[styles.ghostBtn, busy && styles.btnDisabled]}
              disabled={busy}
              onPress={() => {
                void confirm({
                  title: '¿Vaciar pendiente?',
                  message: 'Se borran todos los ítems anotados. No afecta lo ya cobrado ni lo que está en cocina.',
                  confirmLabel: 'Vaciar',
                  destructive: true,
                  presentation: 'overlay',
                }).then(async (ok) => {
                  if (!ok) return;
                  await run(async () => {
                    await staffDraftClear(branchId, diningTableId);
                  }, 'Pendiente vaciado');
                });
              }}
              accessibilityRole="button"
              accessibilityLabel="Vaciar pedido"
            >
              <Text style={styles.ghostLabel}>Vaciar pedido</Text>
            </TouchableOpacity>
          ) : null}

          {paymentPending ? (
        <View style={styles.payNote} testID="hospitality.mesas.pay-pending">
          <Text style={styles.payNoteText}>
            Pago desde el celu en curso. Si llega el webhook se acredita solo. Al cancelar, el link queda sin validez en Vuelto; si el comensal ya pagó, hay que devolverlo desde Mercado Pago.
          </Text>
          {canOperate && onCancelPayment ? (
            <TouchableOpacity
              onPress={onCancelPayment}
              accessibilityRole="button"
              accessibilityLabel="Cancelar pago"
            >
              <Text style={styles.payNoteBtnLabel}>Cancelar pago</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      {canShowLiberate ? (
            <TouchableOpacity
              style={[
                postPayIdle ? styles.primaryBtn : styles.dangerBtn,
                busy && styles.btnDisabled,
              ]}
              disabled={busy}
              onPress={liberate}
              accessibilityRole="button"
              accessibilityLabel="Liberar mesa"
              testID="hospitality.mesas.draft-liberate"
            >
              <Text style={postPayIdle ? styles.primaryLabel : styles.dangerLabel}>
                Liberar mesa
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : (
        <Text style={styles.hintMuted}>Solo lectura — pedile a quien opera el salón.</Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  mozoRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 6, gap: 8 },
  mozoLabel: { ...typography.body, color: ticketColors.ink2 },
  mozoValue: { fontWeight: '700', color: ticketColors.ink },
  mozoBtnLabel: { ...typography.caption, color: '#115E59', fontWeight: '800', textDecorationLine: 'underline' },
  payNote: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: '#E8F7F4', borderWidth: 1, borderColor: 'rgba(13,148,136,0.25)', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 9, marginTop: 8 },
  payNoteText: { flex: 1, ...typography.caption, color: '#115E59' },
  payNoteBtnLabel: { ...typography.caption, color: '#0F766E', fontWeight: '800' },
  box: {
    gap: spacing.md,
  },
  visitMeta: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
  },
  visitMetaMain: { flex: 1, gap: 2 },
  visitTitle: {
    ...typography.title3,
    color: ticketColors.ink,
    fontWeight: '700',
  },
  visitSub: {
    ...typography.caption,
    color: ticketColors.ink2,
    marginTop: 2,
  },
  visitTotalBlock: { alignItems: 'flex-end', minWidth: 88 },
  visitTotalLabel: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '600',
  },
  visitTotalValue: {
    ...typography.title3,
    color: ticketColors.ink,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
  },
  block: {
    gap: spacing.xs,
    padding: spacing.md,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
  },
  draftBlock: {
    borderColor: ticketColors.brandBorder,
    backgroundColor: ticketColors.brandSoft,
    borderStyle: 'dashed',
  },
  signalBlock: {
    borderColor: 'rgba(180, 83, 9, 0.45)',
    backgroundColor: 'rgba(251, 191, 36, 0.18)',
  },
  signalBlockTitle: {
    ...typography.body,
    color: '#9A3412',
    fontWeight: '800',
  },
  readyBlock: {
    borderColor: 'rgba(5, 150, 105, 0.35)',
    backgroundColor: 'rgba(16, 185, 129, 0.08)',
  },
  readyLine: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  blockHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  collapseHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  collapseChevron: {
    ...typography.body,
    color: ticketColors.ink3,
    fontWeight: '700',
  },
  visitSaleRow: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  visitSaleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: 2,
    paddingVertical: spacing.sm,
  },
  visitSaleTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  visitSaleTotal: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
  },
  visitSaleBody: {
    gap: spacing.xs,
    paddingLeft: spacing.sm,
    paddingBottom: spacing.sm,
  },
  moreLink: {
    ...typography.caption,
    color: ticketColors.brandInk,
    fontWeight: '700',
    paddingVertical: 4,
  },
  moreSalesBtn: {
    alignSelf: 'flex-start',
  },
  collapseChevronSpacer: {
    width: 12,
  },
  blockTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
  },
  blockBadge: {
    ...typography.caption,
    color: ticketColors.brandInk,
    fontWeight: '700',
  },
  hint: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  hintMuted: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  emptyPending: {
    gap: spacing.sm,
  },
  sumarLink: {
    alignSelf: 'flex-start',
    paddingVertical: 4,
  },
  sumarLinkLabel: {
    ...typography.body,
    color: ticketColors.brandInk,
    fontWeight: '700',
  },
  signalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: 4,
  },
  signalLabel: {
    ...typography.body,
    color: '#9A3412',
    fontWeight: '700',
    flex: 1,
  },
  ackBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
    backgroundColor: '#C2410C',
  },
  ackBtnLabel: {
    ...typography.caption,
    color: '#FFFFFF',
    fontWeight: '800',
  },
  link: {
    ...typography.body,
    color: ticketColors.brandInk,
    fontWeight: '700',
  },
  lineRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    paddingVertical: 4,
  },
  draftLine: {
    paddingVertical: 4,
  },
  draftLineMain: {
    flex: 1,
    gap: 2,
  },
  draftLineTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  lineTitleRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
  },
  lineName: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
    flex: 1,
    minWidth: 0,
  },
  lineMeta: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  lineWho: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  linePrice: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
    flexShrink: 0,
  },
  coversRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    marginBottom: spacing.md,
    borderRadius: 12,
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.rule,
  },
  coversTextCol: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  coversLabel: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
  },
  coversHint: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  coversStepper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  coversBtn: {
    width: 40,
    height: 40,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ticketColors.paper2,
    borderWidth: 1,
    borderColor: ticketColors.rule,
  },
  coversBtnLabel: {
    fontSize: 22,
    fontWeight: '700',
    color: ticketColors.ink,
    lineHeight: 24,
  },
  coversValue: {
    ...typography.title3,
    fontWeight: '800',
    color: ticketColors.ink,
    minWidth: 36,
    textAlign: 'center',
    fontVariant: ['tabular-nums'],
  },
  coversValueRead: {
    ...typography.title3,
    fontWeight: '800',
    color: ticketColors.ink,
    minWidth: 36,
    textAlign: 'right',
    fontVariant: ['tabular-nums'],
    paddingRight: 4,
  },
  qtyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
  },
  qtyBtn: {
    width: 28,
    height: 28,
    borderRadius: 7,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.rule,
  },
  qtyBtnLabel: {
    fontSize: 16,
    fontWeight: '700',
    color: ticketColors.ink,
    lineHeight: 18,
  },
  qtyValue: {
    ...typography.caption,
    fontWeight: '800',
    color: ticketColors.ink,
    minWidth: 16,
    textAlign: 'center',
    fontVariant: ['tabular-nums'],
  },
  removeBtn: {
    paddingHorizontal: 4,
    paddingVertical: 4,
  },
  removeBtnLabel: {
    fontSize: 12,
    color: ticketColors.bad,
    fontWeight: '700',
  },
  pillKitchen: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    backgroundColor: ticketColors.brandSoft,
    borderWidth: 1,
    borderColor: ticketColors.brandBorderSoft,
  },
  pillKitchenLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: ticketColors.brandInk,
  },
  pillAccount: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.rule,
  },
  pillAccountLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: ticketColors.ink2,
  },
  pillPaid: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    backgroundColor: 'rgba(16, 185, 129, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(5, 150, 105, 0.35)',
  },
  pillPaidLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#047857',
  },
  pillPending: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    backgroundColor: 'rgba(245, 158, 11, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(180, 83, 9, 0.35)',
  },
  pillPendingLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#9A3412',
  },
  pillReady: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    backgroundColor: 'rgba(16, 185, 129, 0.18)',
    borderWidth: 1,
    borderColor: 'rgba(5, 150, 105, 0.45)',
  },
  pillReadyLabel: {
    fontSize: 10,
    fontWeight: '800',
    color: '#047857',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: spacing.xs,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
    marginTop: 2,
  },
  totalLabel: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  totalValue: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
  },
  preCuentaActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  preCuentaBtn: {
    flexGrow: 1,
    minWidth: 140,
    paddingVertical: 10,
    paddingHorizontal: 12,
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.paper,
  },
  preCuentaBtnLabel: {
    ...typography.caption,
    color: ticketColors.ink,
    fontWeight: '800',
  },
  actions: {
    gap: spacing.sm,
  },
  primaryBtn: {
    backgroundColor: ticketColors.gold,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
  },
  primaryLabel: {
    ...typography.body,
    color: ticketColors.goldInk,
    fontWeight: '800',
  },
  ghostBtn: {
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
  },
  ghostLabel: {
    ...typography.body,
    color: ticketColors.ink2,
    fontWeight: '700',
  },
  dangerBtn: {
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(185, 28, 28, 0.35)',
    backgroundColor: '#FEF2F2',
  },
  dangerLabel: {
    ...typography.body,
    color: ticketColors.bad,
    fontWeight: '700',
  },
  btnDisabled: {
    opacity: 0.5,
  },
});
